import { Box, Button, Flex, Text } from '@chakra-ui/react'
import { RefreshCw, TriangleAlert } from 'lucide-react'
import React from 'react'

import { useReportingI18n } from '../../../i18n'

export interface ErrorDisplayProps {
  message: string
  isPersistent?: boolean
  countdown?: number
  onRetry: () => void
  isRetrying?: boolean
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  message,
  isPersistent = false,
  countdown = 0,
  onRetry,
  isRetrying = false,
}) => {
  const { text } = useReportingI18n()

  return (
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="center"
      padding="32px"
      gap="16px"
      backgroundColor={isPersistent ? 'red.subtle' : 'orange.subtle'}
      borderRadius="md"
      border="1px solid"
      borderColor={isPersistent ? 'red.muted' : 'orange.muted'}
      data-testid="queue-dashboard-error"
    >
      <Box
        as={TriangleAlert}
        width="48px"
        height="48px"
        color={isPersistent ? 'red.solid' : 'orange.solid'}
      />

      <Text
        color={isPersistent ? 'red.fg' : 'orange.fg'}
        fontSize="1rem"
        fontWeight="medium"
        textAlign="center"
      >
        {message}
      </Text>

      {countdown > 0 && !isPersistent && (
        <Text color="fg.muted" fontSize="0.875rem">
          {text(
            'queueDashboard.errorDisplay.retrying',
            'Retrying in {{count}}s...',
            {
              count: countdown,
            },
          )}
        </Text>
      )}

      {isPersistent && (
        <Text color="red.fg" fontSize="0.875rem" textAlign="center">
          {text(
            'queueDashboard.errorDisplay.persistentMessage',
            'Automatic refresh has been paused due to repeated failures.',
          )}
        </Text>
      )}

      <Button
        onClick={onRetry}
        loading={isRetrying}
        colorPalette={isPersistent ? 'error' : 'warning'}
        variant="outline"
        size="sm"
        data-testid="queue-dashboard-retry-button"
      >
        <Box as={RefreshCw} width="16px" height="16px" />
        {isRetrying
          ? text('queueDashboard.errorDisplay.retryingButton', 'Retrying...')
          : text('queueDashboard.errorDisplay.retryNow', 'Retry Now')}
      </Button>
    </Flex>
  )
}

export default ErrorDisplay
